import { z } from "zod";
import { channelIdFromSlackLink } from "./slackChannel";

const required = (label: string, max = 500) =>
  z.string().trim().min(1, `${label} is required.`).max(max);
const optionalSecret = (max: number) => z.string().max(max);

function commaSeparatedItems(options: {
  itemLabel: string;
  itemPattern?: RegExp;
  patternMessage?: string;
}) {
  return z
    .string()
    .max(600)
    .superRefine((value, context) => {
      const items = value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      if (items.length > 10) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Use no more than 10 ${options.itemLabel}.`,
        });
      }
      if (items.some((item) => item.length > 50)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Each ${options.itemLabel.replace(/s$/, "")} must be 50 characters or fewer.`,
        });
      }
      if (
        options.itemPattern &&
        items.some((item) => !options.itemPattern?.test(item))
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: options.patternMessage ?? `Use valid ${options.itemLabel}.`,
        });
      }
    });
}

export const azureDevOpsFormSchema = z.object({
  name: required("Connection name", 100),
  organization: required("Organization", 200),
  project: required("Project", 200),
  workItemType: required("Work item type", 100),
  tenantId: required("Tenant ID", 200),
  clientId: required("Client ID", 200),
  clientSecret: optionalSecret(4_000),
});

export const jiraFormSchema = z.object({
  name: required("Connection name", 100),
  projectKey: z
    .string()
    .trim()
    .regex(/^[A-Z][A-Z0-9_]{1,19}$/, "Use a valid uppercase project key."),
  issueType: required("Issue type", 100),
  jql: required("JQL", 500),
});

export const shortcutFormSchema = z.object({
  name: required("Connection name", 100),
  apiToken: optionalSecret(500),
  workflowStateId: z
    .string()
    .trim()
    .regex(/^[1-9]\d*$/, "Use a positive numeric workflow state ID."),
  storyType: z.enum(["feature", "bug", "chore"]),
  searchQuery: required("Search query", 500),
});

export const mondayFormSchema = z.object({
  name: required("Connection name", 100),
  apiToken: optionalSecret(1_000),
  boardId: z.string().trim().regex(/^\d+$/, "Use a numeric board ID."),
  groupId: z.string().trim().max(200),
  createStatusLabel: z.string().trim().max(100),
  mappingRows: z
    .array(
      z.object({
        id: z.string(),
        field: z.string(),
        columnId: z.string().max(200),
      }),
    )
    .max(6),
});

export const linearFormSchema = z.object({
  name: required("Connection name", 100),
  apiKey: optionalSecret(1_000),
  teamId: z.string().trim().uuid("Use a valid team ID."),
  createStateId: z.union([
    z.literal(""),
    z.string().trim().uuid("Use a valid workflow state ID."),
  ]),
});

export const githubFormSchema = z.object({
  name: required("Connection name", 100),
  token: optionalSecret(1_000),
  owner: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_.-]+$/, "Use a valid repository owner.")
    .max(100),
  repository: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_.-]+$/, "Use a valid repository name.")
    .max(100),
  labels: commaSeparatedItems({ itemLabel: "labels" }),
});

export const zendeskFormSchema = z.object({
  name: required("Connection name", 100),
  subdomain: z
    .string()
    .trim()
    .max(63)
    .regex(
      /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/i,
      "Use a valid Zendesk subdomain.",
    ),
  tags: commaSeparatedItems({
    itemLabel: "tags",
    itemPattern: /^[a-z0-9_-]+$/i,
    patternMessage:
      "Tags may contain only letters, numbers, underscores, and hyphens.",
  }),
});

export const hubSpotFormSchema = z.object({
  name: required("Connection name", 100),
});

export const intercomFormSchema = z.object({
  name: required("Connection name", 100),
  region: z.enum(["US", "EU", "AU"]),
});

export const salesforceFormSchema = z.object({
  name: required("Connection name", 100),
  environment: z.enum(["production", "sandbox"]),
});

export const slackFormSchema = z.object({
  name: required("Connection name", 100),
  channelId: z
    .string()
    .trim()
    .max(1000)
    .transform((value, context) => {
      const id = channelIdFromSlackLink(value);
      if (id) return id;
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Paste a valid Slack channel link.",
      });
      return z.NEVER;
    }),
});

export type AzureDevOpsForm = z.infer<typeof azureDevOpsFormSchema>;
export type JiraForm = z.infer<typeof jiraFormSchema>;
export type ShortcutForm = z.infer<typeof shortcutFormSchema>;
export type MondayForm = z.infer<typeof mondayFormSchema>;
export type LinearForm = z.infer<typeof linearFormSchema>;
export type GitHubForm = z.infer<typeof githubFormSchema>;
export type ZendeskForm = z.infer<typeof zendeskFormSchema>;
export type HubSpotForm = z.infer<typeof hubSpotFormSchema>;
export type IntercomForm = z.infer<typeof intercomFormSchema>;
export type SalesforceForm = z.infer<typeof salesforceFormSchema>;
export type SlackForm = z.infer<typeof slackFormSchema>;
